<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import ShipmentFormBody from '@/components/shipment/form/ShipmentFormBody.vue';
import ShipmentStatusBadge from '@/components/shipment/shared/ShipmentStatusBadge.vue';
import { useShipmentEditFlow } from '@/composables/useShipmentEditFlow';
import { useShipmentEditFormFocus } from '@/composables/useShipmentEditFormFocus';
import { useShipmentFormPlannedRangeSync } from '@/composables/useShipmentFormPlannedRangeSync';
import { provideShipmentFormStopsSession } from '@/composables/useShipmentFormStopsSession';
import type { ShipmentFormEditContext } from '@/components/shipment/form/types/shipment-form-page-context';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_MEDIUM,
} from '@/constants/progress-spinner';

const route = useRoute();
const shipmentId = computed(() => Number(route.params.id));

const { stopsPort } = provideShipmentFormStopsSession();

const editFlow = useShipmentEditFlow({
  shipmentId,
  stopsPort,
});

const deviceValue = editFlow.selectedDevicesPickerValue;
const formBodyRef = ref<InstanceType<typeof ShipmentFormBody> | null>(null);

useShipmentFormPlannedRangeSync();

useShipmentEditFormFocus({
  hydrated: editFlow.hydrated,
  canEditTracker: editFlow.canEditTracker,
  canEditMetadata: computed(() => editFlow.capabilities.value.canEditMetadata),
  formBodyRef,
});

const showStopsSection = computed(() => Boolean(editFlow.shipment.value));

const trackerReadOnly = computed(() => !editFlow.canEditTracker.value);

const pageContext = computed<ShipmentFormEditContext>(() => ({
  mode: 'edit',
  routingStrategy: editFlow.routingStrategy.value,
  primaryTrackerPosition: editFlow.primaryTrackerPosition.value,
  trackerPositions: editFlow.trackerPositions.value,
  routesHydrationReady: editFlow.routesHydrationReady.value,
  isFormHydrated: editFlow.hydrated.value,
  trackerLoading: editFlow.trackerLoading.value,
  isMapBootstrapMode: true,
  getStopDisabledFields: editFlow.getStopDisabledFields,
  getStopLockReason: editFlow.getStopLockReason,
  getStopLockBadgeLabel: editFlow.getStopLockBadgeLabel,
  hideWaypointControls: editFlow.hideWaypointControls.value,
}));
</script>

<template>
  <div>
    <div
      v-if="editFlow.shipmentQuery.isLoading.value"
      class="flex flex-col items-center justify-center py-16"
    >
      <ProgressSpinner
        :style="PVT_SPINNER_STYLE_MEDIUM"
        :stroke-width="PVT_SPINNER_STROKE_WIDTH"
      />
      <p class="mt-3 text-sm text-gpx_gray-200">Loading shipment…</p>
    </div>

    <div
      v-else-if="editFlow.shipmentQuery.isError.value"
      class="text-gpx_red-500 text-sm"
    >
      Could not load shipment. Try again or go back.
    </div>

    <template v-else-if="editFlow.shipment.value">
      <ShipmentFormBody
        ref="formBodyRef"
        visible-section="all"
        :general-errors="editFlow.generalErrors.value"
        :show-planned-range="editFlow.capabilities.value.canEditRoute"
        :metadata-read-only="!editFlow.capabilities.value.canEditMetadata"
        :show-stops="showStopsSection"
        :page-context="pageContext"
        :tracker-mode="trackerReadOnly ? 'locked' : 'picker'"
        v-model:devices="deviceValue"
        :device-error="editFlow.generalErrors.value.device"
        :assigned-devices="editFlow.assignedDevices.value"
      >
        <template #header>
          <div class="mb-4">
            <div class="flex items-center gap-2">
              <span class="text-sm text-gpx_gray-200">Status</span>
              <ShipmentStatusBadge
                v-if="editFlow.displayStatus.value"
                :status="editFlow.displayStatus.value"
              />
            </div>
          </div>
        </template>

        <template #footer>
          <div
            class="mt-8 flex flex-wrap items-center justify-end gap-2 border-t border-gpx_gray-800 pt-6"
          >
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              size="small"
              tabindex="-1"
              @click="editFlow.cancel"
            />
            <Button
              v-if="editFlow.capabilities.value.canPublish"
              type="button"
              label="Publish"
              size="small"
              :loading="editFlow.publishBusy.value"
              :disabled="editFlow.saveBusy.value"
              @click="editFlow.publish"
            />
            <Button
              type="button"
              label="Save"
              size="small"
              :loading="editFlow.saveBusy.value"
              :disabled="editFlow.publishBusy.value"
              @click="editFlow.save"
            />
          </div>
        </template>
      </ShipmentFormBody>
    </template>
  </div>
</template>
